import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { ValuesType } from 'utility-types';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';
import { MenuButton } from '~/modules/Elements/Menu';
import Modal from '~/modules/Elements/Modal';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListener } from '~/modules/GameEvents/hooks';
import RemoteMicClient from '~/modules/RemoteMic/Network/Client';
import { NetworkRemoteMicListMessage } from '~/modules/RemoteMic/Network/messages';
import RemoteMicManager from '~/modules/RemoteMic/RemoteMicManager';
import PlayerChangeModal from '~/routes/RemoteMic/Components/PlayerChangeModal';
import PlayerNumberCircle from '~/routes/RemoteMic/Components/PlayerNumberCircle';

function ManagePlayers() {
  useEffect(() => {
    RemoteMicClient.subscribe('remote-mics');

    return () => {
      RemoteMicClient.unsubscribe('remote-mics');
    };
  }, []);

  const [list] = useEventListener(events.remoteMicListUpdated) ?? [[]];

  return (
    <Stack>
      <SectionTitle>Manage players</SectionTitle>
      {list.map((mic) => (
        <Entry mic={mic} key={mic.id} />
      ))}
      {list.length === 0 && <EmptyText>No remote microphones connected</EmptyText>}
    </Stack>
  );
}

const Entry = ({ mic }: { mic: ValuesType<NetworkRemoteMicListMessage['list']> }) => {
  const [open, setOpen] = useState(false);
  const permission = RemoteMicManager.getPermission(mic.id);

  return (
    <Container data-test="manage-players">
      <Modal open={open} onClose={() => setOpen(false)}>
        {permission === 'write' && open && (
          <PlayerChangeModal
            showRemoveButton={true}
            id={mic.id}
            playerNumber={mic.number}
            onModalClose={() => setOpen(false)}
            header={
              <>
                Change <strong className="ph-no-capture">{mic.name}</strong>:
              </>
            }
          />
        )}
      </Modal>
      <RemoteMicEntry
        onClick={() => setOpen(true)}
        data-test="remote-mic-entry"
        data-id={mic.id ?? mic.name}
        size="small">
        <PlayerNumberCircle number={mic.number} />
        <RemoteMicId>{mic.id?.slice(-4) ?? '????'}</RemoteMicId>
        <RemoteMicName className="ph-no-capture" isSelf={mic.id === RemoteMicClient.getClientId()}>
          {mic.name}
        </RemoteMicName>
      </RemoteMicEntry>
    </Container>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: var(--walky-ink);
  font-size: 16px;
  font-weight: 900;
`;

const EmptyText = styled(Typography)`
  color: var(--walky-muted);
  font-size: 13px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RemoteMicEntry = styled(MenuButton)`
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  gap: 10px;
  min-height: 48px !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  font-size: 14px !important;
  text-align: left !important;
`;

const RemoteMicName = styled.div<{ isSelf: boolean }>`
  color: ${(props) => (props.isSelf ? `var(--walky-orange)` : 'var(--walky-ink)')};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const RemoteMicId = styled.span`
  color: var(--walky-muted);
  font-size: 12px;
  font-weight: 900;
`;
export default ManagePlayers;
